import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Crown, Download, LogOut, RefreshCw, Loader2 } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const toCSV = (rows, columns) => {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return `"${s}"`;
  };
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => escape(r[c.key])).join(","))
    .join("\n");
  return `${header}\n${body}`;
};

const downloadCSV = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const PROSPECT_COLS = [
  { key: "created_at", label: "Submitted" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "city", label: "City" },
  { key: "branch", label: "Branch" },
  { key: "motorcycle_type", label: "Motorcycle" },
  { key: "experience", label: "Experience" },
  { key: "message", label: "Message" },
  { key: "reviewed", label: "Reviewed" },
];

const CONTACT_COLS = [
  { key: "created_at", label: "Submitted" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "subject", label: "Subject" },
  { key: "message", label: "Message" },
  { key: "reviewed", label: "Reviewed" },
];

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("prospects");

  const fetchInquiries = async (pw) => {
    const res = await api.post("/admin/inquiries", { password: pw });
    setProspects(res.data.prospects || []);
    setContacts(res.data.contact_messages || []);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      await fetchInquiries(password);
      setAuthed(true);
      toast.success("Welcome, Queen 👑");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Wrong password");
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchInquiries(password);
      toast.success("Refreshed");
    } catch {
      toast.error("Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    setAuthed(false);
    setPassword("");
    setProspects([]);
    setContacts([]);
  };

  const toggleReviewed = async (kind, item) => {
    const next = !item.reviewed;
    // optimistic update
    const setter = kind === "prospect" ? setProspects : setContacts;
    setter((list) =>
      list.map((r) => (r.id === item.id ? { ...r, reviewed: next } : r))
    );
    try {
      await api.patch(`/admin/${kind}/${item.id}/reviewed`, {
        password,
        reviewed: next,
      });
    } catch {
      // revert
      setter((list) =>
        list.map((r) => (r.id === item.id ? { ...r, reviewed: !next } : r))
      );
      toast.error("Could not update");
    }
  };

  const exportProspectsCSV = () => {
    const rows = prospects.map((p) => ({
      ...p,
      created_at: fmtDate(p.created_at),
      reviewed: p.reviewed ? "Yes" : "No",
    }));
    downloadCSV(
      `crown-jewels-prospects-${new Date().toISOString().slice(0, 10)}.csv`,
      toCSV(rows, PROSPECT_COLS)
    );
  };

  const exportContactsCSV = () => {
    const rows = contacts.map((c) => ({
      ...c,
      created_at: fmtDate(c.created_at),
      reviewed: c.reviewed ? "Yes" : "No",
    }));
    downloadCSV(
      `crown-jewels-contacts-${new Date().toISOString().slice(0, 10)}.csv`,
      toCSV(rows, CONTACT_COLS)
    );
  };

  if (!authed) {
    return (
      <main
        className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4"
        data-testid="admin-login-page"
      >
        <Card className="w-full max-w-md bg-[#0c0c0c] border-[#E91E8C]/40">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <Crown className="h-10 w-10 text-[#E91E8C]" />
            </div>
            <CardTitle
              className="font-[Playfair_Display] text-3xl tracking-wide"
              data-testid="admin-title"
            >
              Admin Dashboard
            </CardTitle>
            <p className="text-sm text-white/60 mt-2">
              Crown Jewels Sisterhood Houston
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="admin-pw" className="text-white/80">
                  Password
                </Label>
                <Input
                  id="admin-pw"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="mt-2 bg-black border-[#E91E8C]/40 text-white"
                  data-testid="admin-password-input"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E91E8C] hover:bg-[#c81878] text-white"
                data-testid="admin-login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Enter"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    );
  }

  const total = prospects.length + contacts.length;
  const unreviewedProspects = prospects.filter((p) => !p.reviewed).length;
  const unreviewedContacts = contacts.filter((c) => !c.reviewed).length;

  return (
    <main
      className="min-h-screen bg-[#050505] text-white"
      data-testid="admin-dashboard"
    >
      <header className="border-b border-[#E91E8C]/30 bg-black/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Crown className="h-7 w-7 text-[#E91E8C]" />
            <div>
              <h1 className="font-[Playfair_Display] text-2xl leading-none">
                Admin Dashboard
              </h1>
              <p className="text-xs text-white/50 mt-1">
                {total} total submissions · {unreviewedProspects + unreviewedContacts} new
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-[#E91E8C]/40 text-white hover:bg-[#E91E8C]/10"
              data-testid="admin-refresh-button"
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2 hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" />
              <span className="ml-2 hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10">
          <button
            type="button"
            onClick={() => setTab("prospects")}
            data-testid="tab-prospects"
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              tab === "prospects"
                ? "text-[#E91E8C] border-b-2 border-[#E91E8C]"
                : "text-white/60 hover:text-white"
            }`}
          >
            Prospect Applications
            <Badge
              variant="secondary"
              className="ml-2 bg-[#E91E8C]/20 text-[#E91E8C] border-0"
            >
              {prospects.length}
            </Badge>
          </button>
          <button
            type="button"
            onClick={() => setTab("contacts")}
            data-testid="tab-contacts"
            className={`px-4 py-2 font-medium text-sm transition-colors ${
              tab === "contacts"
                ? "text-[#E91E8C] border-b-2 border-[#E91E8C]"
                : "text-white/60 hover:text-white"
            }`}
          >
            Contact Messages
            <Badge
              variant="secondary"
              className="ml-2 bg-[#E91E8C]/20 text-[#E91E8C] border-0"
            >
              {contacts.length}
            </Badge>
          </button>
        </div>

        {tab === "prospects" && (
          <Section
            title="Prospect Applications"
            subtitle="Women who applied to join the Crown Jewels."
            onExport={exportProspectsCSV}
            exportTestId="export-prospects-csv"
            count={prospects.length}
          >
            {prospects.length === 0 ? (
              <EmptyState message="No prospect applications yet." />
            ) : (
              <ProspectTable
                rows={prospects}
                onToggleReviewed={(item) => toggleReviewed("prospect", item)}
              />
            )}
          </Section>
        )}

        {tab === "contacts" && (
          <Section
            title="Contact Messages"
            subtitle="General inquiries received from the contact form."
            onExport={exportContactsCSV}
            exportTestId="export-contacts-csv"
            count={contacts.length}
          >
            {contacts.length === 0 ? (
              <EmptyState message="No contact messages yet." />
            ) : (
              <ContactTable
                rows={contacts}
                onToggleReviewed={(item) => toggleReviewed("contact", item)}
              />
            )}
          </Section>
        )}
      </div>
    </main>
  );
}

function Section({ title, subtitle, onExport, exportTestId, count, children }) {
  return (
    <div>
      <div className="flex items-start sm:items-center justify-between gap-4 mb-4 flex-col sm:flex-row">
        <div>
          <h2 className="font-[Playfair_Display] text-2xl">{title}</h2>
          <p className="text-sm text-white/50">{subtitle}</p>
        </div>
        <Button
          onClick={onExport}
          disabled={count === 0}
          className="bg-[#E91E8C] hover:bg-[#c81878] text-white"
          data-testid={exportTestId}
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>
      <Card className="bg-[#0c0c0c] border-white/10">
        <CardContent className="p-0 overflow-x-auto">{children}</CardContent>
      </Card>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-16 text-white/50" data-testid="empty-state">
      {message}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`text-left text-xs uppercase tracking-wider font-semibold text-white/60 px-4 py-3 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-3 text-sm align-top ${className}`}>{children}</td>
  );
}

function ReviewedToggle({ row, onToggle, testIdPrefix }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <Checkbox
        checked={!!row.reviewed}
        onCheckedChange={() => onToggle(row)}
        className="border-[#E91E8C]/60 data-[state=checked]:bg-[#E91E8C] data-[state=checked]:border-[#E91E8C]"
        data-testid={`${testIdPrefix}-${row.id}`}
      />
      <span
        className={`text-xs ${row.reviewed ? "text-[#E91E8C]" : "text-white/40"}`}
      >
        {row.reviewed ? "Reviewed" : "New"}
      </span>
    </label>
  );
}

function ProspectTable({ rows, onToggleReviewed }) {
  return (
    <table className="w-full" data-testid="prospects-table">
      <thead className="border-b border-white/10 bg-black/40">
        <tr>
          <Th>Submitted</Th>
          <Th>Name</Th>
          <Th>Contact</Th>
          <Th>City</Th>
          <Th>Branch</Th>
          <Th>Motorcycle</Th>
          <Th>Experience / Message</Th>
          <Th>Status</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr
            key={p.id}
            className={`border-b border-white/5 ${
              p.reviewed ? "opacity-60" : ""
            }`}
            data-testid={`prospect-row-${p.id}`}
          >
            <Td className="whitespace-nowrap text-white/70">
              {fmtDate(p.created_at)}
            </Td>
            <Td className="font-medium">{p.name}</Td>
            <Td>
              <div className="text-white/90">{p.email}</div>
              <div className="text-white/50 text-xs">{p.phone}</div>
            </Td>
            <Td className="text-white/80">{p.city}</Td>
            <Td>
              <Badge className="bg-[#E91E8C]/20 text-[#E91E8C] border-[#E91E8C]/40">
                {p.branch}
              </Badge>
            </Td>
            <Td className="text-white/80 max-w-[160px]">
              {p.motorcycle_type || "—"}
            </Td>
            <Td className="max-w-[360px]">
              <div className="text-white/90 whitespace-pre-wrap">
                {p.experience}
              </div>
              {p.message && (
                <div className="text-white/50 text-xs mt-1 whitespace-pre-wrap">
                  {p.message}
                </div>
              )}
            </Td>
            <Td>
              <ReviewedToggle
                row={p}
                onToggle={onToggleReviewed}
                testIdPrefix="prospect-reviewed"
              />
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ContactTable({ rows, onToggleReviewed }) {
  return (
    <table className="w-full" data-testid="contacts-table">
      <thead className="border-b border-white/10 bg-black/40">
        <tr>
          <Th>Submitted</Th>
          <Th>Name</Th>
          <Th>Email</Th>
          <Th>Subject</Th>
          <Th>Message</Th>
          <Th>Status</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr
            key={c.id}
            className={`border-b border-white/5 ${
              c.reviewed ? "opacity-60" : ""
            }`}
            data-testid={`contact-row-${c.id}`}
          >
            <Td className="whitespace-nowrap text-white/70">
              {fmtDate(c.created_at)}
            </Td>
            <Td className="font-medium">{c.name}</Td>
            <Td className="text-white/90">{c.email}</Td>
            <Td className="text-white/90">{c.subject}</Td>
            <Td className="max-w-[480px] text-white/80 whitespace-pre-wrap">
              {c.message}
            </Td>
            <Td>
              <ReviewedToggle
                row={c}
                onToggle={onToggleReviewed}
                testIdPrefix="contact-reviewed"
              />
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
